import React from 'react';
import PropTypes from 'prop-types';
import { LuCloudUpload, LuDownload, LuLoaderCircle, LuUpload, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CARD, HelpNote } from '../components/ProfileUi';
import { FileTypeTile, formatFileSize } from './CvDocumentList';

// Şablonlar public/templates altında
const TEMPLATES = {
  'CV-TR': { href: '/templates/Bakanlik_Ozgecmis_Formu.docx', key: 'templateTr' },
  'CV-EN': { href: '/templates/TransCelerate_Abbreviated_CV_Template.docx', key: 'templateEn' },
};

/**
 * Picking and uploading the file(s). For one-file types the area is locked
 * while a document exists (delete it first), as before.
 */
const CvUploadCard = ({ documentType, allowMultiple, inputId, file, files, hasDocument, uploading, dragging, dragHandlers, onSelect, onRemoveFile, onClear, onUpload }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.cv.${key}`, vars);
  const typeLabel = (key) => t(`profilePage.cv.types.${documentType}.${key}`);
  const locked = !allowMultiple && hasDocument;
  const selectedCount = allowMultiple ? files.length : file ? 1 : 0;
  const template = TEMPLATES[documentType];

  return (
    <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
      <div>
        <h3 className="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{typeLabel('upload')}</h3>
        <p className="tw-mt-1 tw-text-sm tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{typeLabel('description')}</p>
      </div>

      <ul className="tw-flex tw-flex-wrap tw-gap-1.5">
        {['maxSize', 'formats', allowMultiple ? 'multiple' : 'single'].map((rule) => (
          <li key={rule} className="tw-rounded-full tw-bg-slate-500/[0.08] tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">
            {label(`rules.${rule}`)}
          </li>
        ))}
      </ul>

      {template && (
        <a
          href={template.href}
          download={template.href.split('/').pop()}
          className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-sky-500/20 tw-bg-sky-500/[0.06] tw-px-3.5 tw-py-3 tw-text-sm tw-font-semibold tw-text-sky-800 tw-transition-colors hover:tw-bg-sky-500/[0.1] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-sky-500/40 dark:tw-text-sky-200"
        >
          <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
          <span className="tw-min-w-0">
            <span className="tw-block">{label(template.key)}</span>
            <span className="tw-block tw-text-xs tw-font-normal tw-opacity-80">{label('templateHint')}</span>
          </span>
        </a>
      )}

      {locked && <HelpNote tone="warning">{label('locked', { type: typeLabel('name') })}</HelpNote>}

      <label
        htmlFor={inputId}
        {...dragHandlers}
        className={`tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-7 tw-text-center tw-transition-colors ${
          locked
            ? 'tw-cursor-not-allowed tw-border-slate-200 tw-opacity-60 dark:tw-border-white/10'
            : dragging
              ? 'tw-cursor-copy tw-border-teal-500 tw-bg-teal-500/[0.07]'
              : 'tw-cursor-pointer tw-border-slate-300 tw-bg-slate-50/60 hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] dark:tw-border-white/15 dark:tw-bg-white/[0.02]'
        }`}
      >
        <span className="tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-2xl tw-bg-teal-500/10 tw-text-teal-600 dark:tw-text-teal-300">
          <LuCloudUpload aria-hidden="true" className="tw-h-6 tw-w-6" />
        </span>
        <span className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">
          {dragging ? label('dropNow') : selectedCount > 1 ? label('selectedCount', { count: selectedCount }) : file && !allowMultiple ? file.name : label('drop')}
        </span>
        <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          {!allowMultiple && file ? formatFileSize(file.size) : label('dropHint')}
        </span>
        <input
          id={inputId}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={onSelect}
          multiple={allowMultiple}
          disabled={locked}
          className="tw-sr-only"
        />
      </label>

      {allowMultiple && files.length > 0 && (
        <ul className="tw-space-y-1.5">
          {files.map((item, index) => (
            <li key={`${item.name}-${index}`} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-py-1.5 tw-pl-2 tw-pr-1.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
              <FileTypeTile type={item.name.split('.').pop()} small />
              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{item.name}</span>
              <span className="tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{formatFileSize(item.size)}</span>
              <button
                type="button"
                onClick={() => onRemoveFile(index)}
                title={label('remove')}
                aria-label={`${label('remove')}: ${item.name}`}
                className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-slate-500 tw-transition-colors hover:tw-bg-rose-500/10 hover:tw-text-rose-600 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-rose-500/40 dark:tw-text-slate-400"
              >
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
        {selectedCount > 0 && (
          <Button variant="ghost" onClick={onClear} disabled={uploading}>
            <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
            {allowMultiple ? label('removeAll') : label('remove')}
          </Button>
        )}
        <Button
          variant="primary"
          onClick={onUpload}
          disabled={(allowMultiple ? files.length === 0 : !file) || uploading || locked}
          className="max-sm:tw-flex-1"
        >
          {uploading ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuUpload aria-hidden="true" className="tw-h-4 tw-w-4" />}
          {uploading ? label('uploading') : typeLabel('upload')}
        </Button>
      </div>
    </section>
  );
};

CvUploadCard.propTypes = {
  documentType: PropTypes.string.isRequired,
  allowMultiple: PropTypes.bool,
  inputId: PropTypes.string.isRequired,
  file: PropTypes.object,
  files: PropTypes.array.isRequired,
  hasDocument: PropTypes.bool,
  uploading: PropTypes.bool,
  dragging: PropTypes.bool,
  dragHandlers: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};

export default CvUploadCard;
